import { useState, useEffect } from "react";
import Modal from "../ui/Modal";
import Select from "../ui/Select";
import { Plus, Shield, MapPin, UserPlus, Trash2, Edit2, X, QrCode } from "lucide-react";
import PropertyQrPosterModal from "./PropertyQrPosterModal";
import {
  getSocietyGates,
  createSocietyGate,
  updateSocietyGate,
  deleteSocietyGate,
  assignStaffToGate,
  unassignStaffFromGate,
  getSocietyStaff,
} from "../../services/societyService";
import {
  normalizePropertyType,
  PROPERTY_CATEGORIES,
} from "../../utils/propertyStructure";
import { getCurrentRoleKey } from "../../services/authService";

const PropertyGates = ({ societyId, propertyType, propertyName }) => {
  const normalizedCategory = normalizePropertyType(propertyType);
  const isIndustrial = normalizedCategory === PROPERTY_CATEGORIES.INDUSTRIAL;
  const isCommercial = normalizedCategory === PROPERTY_CATEGORIES.COMMERCIAL;

  const roleKey = getCurrentRoleKey();
  const isSuperAdmin = roleKey === "super-admin";
  const isAdmin = roleKey === "admin";
  const isSocietyAdmin = roleKey === "society-admin";
  const isPropertyManager = roleKey === "property-manager" || roleKey === "manager";
  const canManageGates = isSuperAdmin || isAdmin || isSocietyAdmin || isPropertyManager;

  const defaultGateType = isIndustrial
    ? "Main Factory Gate"
    : isCommercial
      ? "Main Entrance"
      : "Main";

  const [gates, setGates] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newGateData, setNewGateData] = useState({ name: "", type: defaultGateType });

  const [assignGate, setAssignGate] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState("");

  const [gateToEdit, setGateToEdit] = useState(null);
  const [editGateData, setEditGateData] = useState({ name: "", type: defaultGateType });
  const [updatingGate, setUpdatingGate] = useState(false);

  const [gateToDelete, setGateToDelete] = useState(null);
  const [deletingGate, setDeletingGate] = useState(false);

  // QR Standee Poster Modal
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedGateForQr, setSelectedGateForQr] = useState("");

  useEffect(() => {
    if (societyId) {
      fetchGates();
      fetchStaff();
    }
  }, [societyId]);

  const fetchGates = async () => {
    setLoading(true);
    const result = await getSocietyGates(societyId);
    setGates(Array.isArray(result) ? result : result?.data || []);
    setLoading(false);
  };

  const fetchStaff = async () => {
    try {
      const result = await getSocietyStaff(societyId);
      setStaffList(Array.isArray(result) ? result : result?.data || []);
    } catch {
      setStaffList([]);
    }
  };

  const handleAddGate = async () => {
    if (!newGateData.name.trim()) return;
    const res = await createSocietyGate(societyId, newGateData);
    if (res || res === undefined) {
      fetchGates();
      setIsAddModalOpen(false);
      setNewGateData({ name: "", type: defaultGateType });
    }
  };

  const handleAssignStaff = async () => {
    if (!selectedStaff.trim() || !assignGate) return;
    const res = await assignStaffToGate(societyId, assignGate.id, selectedStaff);
    if (res || res === undefined) {
      fetchGates();
      setAssignGate(null);
      setSelectedStaff("");
    }
  };

  const handleConfirmDeleteGate = async () => {
    if (!gateToDelete) return;
    try {
      setDeletingGate(true);
      await deleteSocietyGate(societyId, gateToDelete.id || gateToDelete.gate_id);
      setGateToDelete(null);
      fetchGates();
    } catch (err) {
      console.error("Delete gate failed:", err);
    } finally {
      setDeletingGate(false);
    }
  };

  const openEditGateModal = (g) => {
    setGateToEdit(g);
    setEditGateData({
      name: g.name || g.gate_name || "",
      type: g.type || g.gate_type || defaultGateType,
    });
  };

  const handleSaveGateEdit = async () => {
    if (!gateToEdit || !editGateData.name.trim()) return;
    try {
      setUpdatingGate(true);
      await updateSocietyGate(societyId, gateToEdit.id || gateToEdit.gate_id, {
        name: editGateData.name.trim(),
        gate_name: editGateData.name.trim(),
        type: editGateData.type,
        gate_type: editGateData.type,
      });
      setGateToEdit(null);
      fetchGates();
    } catch (err) {
      console.error("Update gate failed:", err);
    } finally {
      setUpdatingGate(false);
    }
  };

  const handleUnassignStaff = async (gateId, staffId) => {
    if (!gateId) return;
    try {
      await unassignStaffFromGate(societyId, gateId, staffId);
      fetchGates();
    } catch (err) {
      console.error("Unassign staff failed:", err);
    }
  };

  const gateTypes = isIndustrial
    ? [
      "Main Factory Gate",
      "Loading & Freight Bay",
      "Turnstile / Employee Pedestrian",
      "Material Inward & Dispatch",
      "Emergency Gate",
    ]
    : isCommercial
      ? [
        "Main Entrance",
        "Service & Goods Delivery",
        "Customer Pedestrian",
        "Emergency Exit",
      ]
      : ["Main", "Service", "Pedestrian", "Emergency"];

  return (
    <div
      style={{
        padding: "24px",
        background: "#fff",
        borderRadius: "8px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <div>
          <h3
            style={{
              color: "#0f172a",
              marginBottom: "6px",
              fontSize: "18px",
              fontWeight: "700",
              fontFamily: "var(--font-heading, Montserrat, sans-serif)",
            }}
          >
            {isIndustrial
              ? "Gates & Loading Bays"
              : isCommercial
                ? "Gates & Commercial Entrances"
                : "Gates & Access Points"}
          </h3>
          <p style={{ color: "#64748b", margin: 0, fontSize: "13.5px" }}>
            {isIndustrial
              ? "Manage factory access gates, freight & loading docks, and assigned security personnel."
              : isCommercial
                ? "Manage customer entrances, service loading docks, and security personnel."
                : "Manage physical entry points and map security personnel."}
          </p>
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <button
            type="button"
            onClick={() => {
              setSelectedGateForQr("");
              setQrModalOpen(true);
            }}
            style={{
              padding: "10px 16px",
              background: "#fff",
              color: "var(--color-dark-bg, #27306f)",
              border: "1px solid #cbd5e1",
              borderRadius: "6px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontWeight: "600",
              fontSize: "13.5px",
            }}
          >
            <QrCode size={16} /> Generate Gate QR
          </button>
          <button
            onClick={() => {
              setNewGateData({ name: "", type: gateTypes[0] });
              setIsAddModalOpen(true);
            }}
            style={{
              padding: "10px 16px",
              background: "var(--color-dark-bg, #27306f)",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontWeight: "600",
              fontSize: "13.5px",
            }}
          >
            <Plus size={16} /> {isIndustrial ? "Add Gate / Loading Bay" : "Add Gate"}
          </button>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          gap: "20px",
        }}
      >
        {loading ? (
          <div style={{ gridColumn: "1 / -1", padding: "40px", textAlign: "center", color: "#64748b" }}>
            Loading gates & bays...
          </div>
        ) : gates.length === 0 ? (
          <div style={{ gridColumn: "1 / -1", padding: "40px", textAlign: "center", color: "#64748b" }}>
            No gates or loading bays configured yet.
          </div>
        ) : (
          gates.map((gate) => (
            <div
              key={gate.id}
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                padding: "20px",
                background: "#f8fafc",
                position: "relative",
              }}
            >
              {canManageGates && (
                <div
                  style={{
                    position: "absolute",
                    top: "16px",
                    right: "16px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedGateForQr(gate.id);
                      setQrModalOpen(true);
                    }}
                    title={`Generate QR for ${gate.name}`}
                    style={{
                      background: "#fff",
                      border: "1px solid #e2e8f0",
                      borderRadius: "6px",
                      padding: "6px 8px",
                      color: "var(--color-dark-bg, #27306f)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 150ms ease",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#eff6ff";
                      e.currentTarget.style.borderColor = "#bfdbfe";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#fff";
                      e.currentTarget.style.borderColor = "#e2e8f0";
                    }}
                  >
                    <QrCode size={15} />
                  </button>
                  <button
                    type="button"
                    onClick={() => openEditGateModal(gate)}
                    title={`Edit ${gate.name}`}
                    style={{
                      background: "#fff",
                      border: "1px solid #e2e8f0",
                      borderRadius: "6px",
                      padding: "6px 8px",
                      color: "var(--color-dark-bg)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 150ms ease",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#eff6ff";
                      e.currentTarget.style.borderColor = "#bfdbfe";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#fff";
                      e.currentTarget.style.borderColor = "#e2e8f0";
                    }}
                  >
                    <Edit2 size={15} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setGateToDelete(gate)}
                    title={`Delete ${gate.name}`}
                    style={{
                      background: "#fff",
                      border: "1px solid #fee2e2",
                      borderRadius: "6px",
                      padding: "6px 8px",
                      color: "#dc2626",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 150ms ease",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#fef2f2";
                      e.currentTarget.style.borderColor = "#fca5a5";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#fff";
                      e.currentTarget.style.borderColor = "#fee2e2";
                    }}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                  marginBottom: "16px",
                  paddingRight: canManageGates ? "72px" : "0",
                }}
              >
                <div
                  style={{
                    background: "rgba(39, 48, 111, 0.08)",
                    padding: "10px",
                    borderRadius: "8px",
                    color: "var(--color-dark-bg, #27306f)",
                    flexShrink: 0,
                  }}
                >
                  <MapPin size={24} />
                </div>
                <div>
                  <h4 style={{ margin: "0 0 4px 0", color: "#0f172a", fontSize: "16px", fontWeight: "700" }}>
                    {gate.name}
                  </h4>
                  <span
                    style={{
                      background: "#e2e8f0",
                      color: "#475569",
                      padding: "2px 8px",
                      borderRadius: "12px",
                      fontSize: "12px",
                      fontWeight: "600",
                    }}
                  >
                    {gate.type}
                  </span>
                </div>
              </div>

              <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: "16px" }}>
                <h5
                  style={{
                    margin: "0 0 12px 0",
                    color: "#475569",
                    fontSize: "13px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontWeight: "600",
                  }}
                >
                  <Shield size={14} /> Assigned Security ({(gate.staff || []).length})
                </h5>

                {(gate.staff || []).length > 0 ? (
                  <ul
                    style={{
                      listStyle: "none",
                      padding: 0,
                      margin: "0 0 16px 0",
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    {(gate.staff || []).map((s, i) => {
                      const staffName = typeof s === "object" ? (s.label || s.full_name || s.name) : s;
                      const staffId = typeof s === "object" ? (s.guard_user_id || s.id) : null;

                      return (
                        <li
                          key={i}
                          style={{
                            background: "#fff",
                            border: "1px solid #cbd5e1",
                            padding: "8px 12px",
                            borderRadius: "6px",
                            fontSize: "13px",
                            color: "#334155",
                            fontWeight: "500",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "8px",
                          }}
                        >
                          <span style={{ display: "flex", alignItems: "center", gap: "6px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            <Shield size={13} color="var(--color-dark-bg)" style={{ flexShrink: 0 }} />
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{staffName}</span>
                          </span>
                          {canManageGates && (
                            <button
                              type="button"
                              title={`Unassign ${staffName}`}
                              onClick={() => handleUnassignStaff(gate.id, staffId)}
                              style={{
                                background: "transparent",
                                border: "none",
                                color: "#94a3b8",
                                cursor: "pointer",
                                padding: "2px",
                                display: "flex",
                                alignItems: "center",
                                borderRadius: "4px",
                                flexShrink: 0,
                                transition: "color 150ms ease",
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.color = "#dc2626")}
                              onMouseLeave={(e) => (e.currentTarget.style.color = "#94a3b8")}
                            >
                              <X size={14} />
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p
                    style={{
                      fontSize: "13px",
                      color: "#94a3b8",
                      margin: "0 0 16px 0",
                      fontStyle: "italic",
                    }}
                  >
                    No personnel assigned to this access point.
                  </p>
                )}

                <button
                  onClick={() => setAssignGate(gate)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    background: "#fff",
                    border: "1px dashed #cbd5e1",
                    color: "var(--color-dark-bg, #27306f)",
                    borderRadius: "6px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    fontWeight: "600",
                    fontSize: "13px",
                  }}
                >
                  <UserPlus size={14} /> Assign Personnel
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Gate Modal */}
      {isAddModalOpen && (
        <Modal
          isOpen={true}
          onClose={() => setIsAddModalOpen(false)}
          title={isIndustrial ? "Add Gate or Loading Bay" : "Add New Gate"}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "600",
                  color: "#0f172a",
                  fontSize: "14px",
                }}
              >
                {isIndustrial ? "Gate / Loading Bay Name" : "Gate Name"}
              </label>
              <input
                type="text"
                placeholder={
                  isIndustrial
                    ? "e.g. Loading Bay 2 / North Logistics Gate"
                    : isCommercial
                      ? "e.g. Main Mall Gate / North Service Entry"
                      : "e.g. West Wing Exit / Main Gate"
                }
                value={newGateData.name}
                onChange={(e) => setNewGateData({ ...newGateData, name: e.target.value })}
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  fontSize: "14px",
                  fontFamily: "inherit",
                }}
              />
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "600",
                  color: "#0f172a",
                  fontSize: "14px",
                }}
              >
                Category / Type
              </label>
              <Select
                value={newGateData.type}
                onChange={(e) => setNewGateData({ ...newGateData, type: e.target.value })}
                options={gateTypes}
              />
            </div>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                marginTop: "8px",
              }}
            >
              <button
                onClick={() => setIsAddModalOpen(false)}
                style={{
                  padding: "8px 16px",
                  background: "#fff",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "600",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddGate}
                disabled={!newGateData.name.trim()}
                style={{
                  padding: "8px 16px",
                  background: "var(--color-dark-bg, #27306f)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: newGateData.name.trim() ? "pointer" : "not-allowed",
                  opacity: newGateData.name.trim() ? 1 : 0.5,
                  fontWeight: "600",
                }}
              >
                Create Access Point
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Assign Staff Modal */}
      {assignGate && (
        <Modal
          isOpen={true}
          onClose={() => setAssignGate(null)}
          title={`Assign Staff to ${assignGate.name}`}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <p style={{ margin: 0, color: "#64748b", fontSize: "14px" }}>
              Select a security personnel or dock supervisor to assign to this access point.
            </p>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "600",
                  color: "#0f172a",
                  fontSize: "14px",
                }}
              >
                Personnel
              </label>
              <Select
                value={selectedStaff}
                onChange={(e) => setSelectedStaff(e.target.value)}
                placeholder="-- Select Personnel --"
                options={
                  staffList.length > 0
                    ? staffList.map((st) => ({
                      value: st.name || st.full_name || st.id,
                      label: `${st.name || st.full_name || "Staff"} (${st.role || st.designation || (isIndustrial ? "Plant Security" : "Security")})`,
                    }))
                    : [{ value: "", label: "No personnel found (Add staff via Staff & Security module)" }]
                }
              />
            </div>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                marginTop: "8px",
              }}
            >
              <button
                onClick={() => setAssignGate(null)}
                style={{
                  padding: "8px 16px",
                  background: "#fff",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "600",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAssignStaff}
                disabled={!selectedStaff}
                style={{
                  padding: "8px 16px",
                  background: "var(--color-dark-bg, #27306f)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: selectedStaff ? "pointer" : "not-allowed",
                  opacity: selectedStaff ? 1 : 0.5,
                  fontWeight: "600",
                }}
              >
                Assign
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Gate Modal */}
      {gateToDelete && (
        <Modal
          isOpen={true}
          onClose={() => setGateToDelete(null)}
          title="Confirm Gate Deletion"
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <p style={{ margin: 0, color: "#475569", fontSize: "14px", lineHeight: "1.5" }}>
              Are you sure you want to remove <strong>{gateToDelete.name}</strong>?
              <br />
              <span style={{ fontSize: "13px", color: "#64748b" }}>
                Any historical visitor records and vehicle entry logs associated with this gate will remain preserved in the audit archives.
              </span>
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "8px" }}>
              <button
                type="button"
                onClick={() => setGateToDelete(null)}
                disabled={deletingGate}
                style={{
                  padding: "8px 16px",
                  background: "#f1f5f9",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#475569",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteGate}
                disabled={deletingGate}
                style={{
                  padding: "8px 16px",
                  background: "#dc2626",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#fff",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <Trash2 size={14} />
                {deletingGate ? "Deleting..." : "Delete Gate"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Gate Modal */}
      {gateToEdit && (
        <Modal
          isOpen={true}
          onClose={() => setGateToEdit(null)}
          title={isIndustrial ? "Edit Gate or Loading Bay" : "Edit Gate"}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "600",
                  color: "#0f172a",
                  fontSize: "14px",
                }}
              >
                {isIndustrial ? "Gate / Loading Bay Name" : "Gate Name"}
              </label>
              <input
                type="text"
                value={editGateData.name}
                onChange={(e) => setEditGateData({ ...editGateData, name: e.target.value })}
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  fontSize: "14px",
                  fontFamily: "inherit",
                }}
              />
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "600",
                  color: "#0f172a",
                  fontSize: "14px",
                }}
              >
                Gate Classification Type
              </label>
              <Select
                value={editGateData.type}
                onChange={(e) => setEditGateData({ ...editGateData, type: e.target.value })}
                options={gateTypes}
              />
            </div>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                marginTop: "8px",
              }}
            >
              <button
                type="button"
                onClick={() => setGateToEdit(null)}
                disabled={updatingGate}
                style={{
                  padding: "8px 16px",
                  background: "#fff",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "600",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveGateEdit}
                disabled={updatingGate || !editGateData.name.trim()}
                style={{
                  padding: "8px 16px",
                  background: "var(--color-dark-bg)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: updatingGate || !editGateData.name.trim() ? "not-allowed" : "pointer",
                  fontWeight: "600",
                }}
              >
                {updatingGate ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* QR Poster Generator Modal */}
      <PropertyQrPosterModal
        isOpen={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        societyId={societyId}
        propertyName={propertyName}
        gates={gates}
        initialGateId={selectedGateForQr}
      />
    </div>
  );
};

export default PropertyGates;
